import { FunboxWordsFrequency, Wordset } from "../wordset";
import * as GetText from "../../utils/generate";
import { Config } from "../../config/store";
import { setConfig, toggleFunbox } from "../../config/setters";
import * as Misc from "../../utils/misc";
import * as Strings from "../../utils/strings";
import { randomIntFromRange } from "@monkeytype/util/numbers";
import * as Arrays from "../../utils/arrays";
import { save } from "./funbox-memory";
import * as TTS from "../tts";
import {
  showNoticeNotification,
  showErrorNotification,
} from "../../states/notifications";
import * as DDR from "../../utils/ddr";
import * as TestWords from "../test-words";
import { getPoem } from "../poetry";
import * as JSONData from "../../utils/json-data";
import { getSection } from "../wikipedia";
import * as WeakSpot from "../weak-spot";
import * as IPAddresses from "../../utils/ip-addresses";
import {
  getActiveWordIndex,
  setReadAheadDisabled,
  setWordsHidden,
  setWordsWrapperHidden,
  setLayoutfluidTimerText,
  setLayoutfluidTimerVisible,
  setMemoryTimerDuration,
  highlightKeymapKey,
} from "../../states/test";
import { WordGenError } from "../../utils/word-gen-error";
import { FunboxName, KeymapLayout, Layout } from "@monkeytype/schemas/configs";
import { Language, LanguageObject } from "@monkeytype/schemas/languages";

/** Typed input, passed in so funboxes never read the EventLog. */
export type FunboxInput = {
  current: () => string;
  forWord: (wordIndex: number) => string;
};

export type FunboxFunctions = {
  getWord?: (wordset?: Wordset, wordIndex?: number) => string;
  punctuateWord?: (word: string) => string;
  withWords?: (words?: string[]) => Promise<Wordset | PolyglotWordset>;
  alterText?: (word: string, wordIndex: number, wordsBound: number) => string;
  applyConfig?: () => void;
  rememberSettings?: () => void;
  toggleScript?: (params: string[]) => void;
  pullSection?: (language?: Language) => Promise<JSONData.Section | false>;
  handleSpace?: (input: FunboxInput) => void;
  getEmulatedChar?: (event: KeyboardEvent) => string | null;
  handleKeydown?: (event: KeyboardEvent, input: FunboxInput) => Promise<void>;
  getResultContent?: () => string;
  start?: () => void;
  restart?: () => void;
  getWordsFrequencyMode?: () => FunboxWordsFrequency;
};

async function readAheadHandleKeydown(
  event: KeyboardEvent,
  input: FunboxInput,
): Promise<void> {
  const currentInput = input.current();
  const currentWord = TestWords.words.getCurrent();

  if (!currentWord) {
    return;
  }

  const inputCurrentChar = currentInput.slice(-1);
  const wordCurrentChar = currentWord.display.slice(
    currentInput.length - 1,
    currentInput.length,
  );
  const isCorrect = inputCurrentChar === wordCurrentChar;

  if (
    event.key === "Backspace" &&
    !isCorrect &&
    (currentInput !== "" ||
      input.forWord(getActiveWordIndex() - 1) !==
        TestWords.words.get(getActiveWordIndex() - 1)?.textWithCommit ||
      Config.freedomMode)
  ) {
    setReadAheadDisabled(true);
  } else if (event.key === " ") {
    setReadAheadDisabled(false);
  }
}

//todo move to its own file
class CharDistribution {
  public chars: Record<string, number>;
  public count: number;
  constructor() {
    this.chars = {};
    this.count = 0;
  }

  public addChar(char: string): void {
    this.count++;
    if (char in this.chars) {
      (this.chars[char] as number) += 1;
    } else {
      this.chars[char] = 1;
    }
  }

  public randomChar(): string {
    const randomIndex = randomIntFromRange(0, this.count - 1);
    let runningCount = 0;
    for (const [char, charCount] of Object.entries(this.chars)) {
      runningCount += charCount;
      if (runningCount > randomIndex) {
        return char;
      }
    }

    return Object.keys(this.chars)[0] as string;
  }
}
const prefixSize = 2;
class PseudolangWordGenerator extends Wordset {
  public ngrams: Record<string, CharDistribution> = {};
  constructor(words: string[]) {
    super(words);
    // Can generate an unbounded number of words in theory.
    this.length = Infinity;

    for (let word of words) {
      // Mark the end of each word with a space.
      word += " ";
      let prefix = "";
      for (const c of word) {
        // Add `c` to the distribution of chars that can come after `prefix`.
        if (!(prefix in this.ngrams)) {
          this.ngrams[prefix] = new CharDistribution();
        }
        (this.ngrams[prefix] as CharDistribution).addChar(c);
        prefix = (prefix + c).slice(-prefixSize);
      }
    }
  }

  public override randomWord(): string {
    let word = "";
    for (;;) {
      const prefix = word.slice(-prefixSize);
      const charDistribution = this.ngrams[prefix];
      if (!charDistribution) {
        // This shouldn't happen if this.ngrams is complete. If it does
        // somehow, start generating a new word.
        word = "";
        continue;
      }
      // Pick a random char from the distribution that comes after `prefix`.
      const nextChar = charDistribution.randomChar();
      if (nextChar === " ") {
        // A space marks the end of the word, so stop generating and return.
        break;
      }
      word += nextChar;
    }
    return word;
  }
}

export class PolyglotWordset extends Wordset {
  public wordsWithLanguage: Map<string, Language>;
  public languageProperties: Map<Language, JSONData.LanguageProperties>;

  constructor(
    wordsWithLanguage: Map<string, Language>,
    languageProperties: Map<Language, JSONData.LanguageProperties>,
  ) {
    // build and shuffle the word array
    const wordArray = Array.from(wordsWithLanguage.keys());
    Arrays.shuffle(wordArray);
    super(wordArray);
    this.wordsWithLanguage = wordsWithLanguage;
    this.languageProperties = languageProperties;
  }
}

const list: Partial<Record<FunboxName, FunboxFunctions>> = {
  "58008": {
    getWord(): string {
      let num = GetText.getNumbers(7);
      if (Config.language.startsWith("kurdish")) {
        num = Misc.convertNumberToArabic(num);
      } else if (Config.language.startsWith("nepali")) {
        num = Misc.convertNumberToNepali(num);
      }
      return num;
    },
    punctuateWord(word: string): string {
      if (word.length > 3) {
        if (Math.random() < 0.5) {
          word = Strings.replaceCharAt(
            word,
            randomIntFromRange(1, word.length - 2),
            ".",
          );
        }
        if (Math.random() < 0.75) {
          const index = randomIntFromRange(1, word.length - 2);
          if (
            word[index - 1] !== "." &&
            word[index + 1] !== "." &&
            word[index + 1] !== "0"
          ) {
            const special = Arrays.randomElementFromArray(["/", "*", "-", "+"]);
            word = Strings.replaceCharAt(word, index, special);
          }
        }
      }
      return word;
    },
    rememberSettings(): void {
      save("numbers", Config.numbers);
    },
    getEmulatedChar(event: KeyboardEvent): string | null {
      if (event.key === "Enter") {
        return " ";
      }
      return null;
    },
  },
  simon_says: {
    applyConfig(): void {
      setConfig("keymapMode", "next", {
        nosave: true,
      });
    },
    rememberSettings(): void {
      save("keymapMode", Config.keymapMode);
    },
  },
  tts: {
    applyConfig(): void {
      setConfig("keymapMode", "off", {
        nosave: true,
      });
    },
    rememberSettings(): void {
      save("keymapMode", Config.keymapMode);
    },
    toggleScript(params: string[]): void {
      if (window.speechSynthesis === undefined) {
        showErrorNotification("Failed to load text-to-speech script");
        return;
      }
      if (params[0] !== undefined) void TTS.speak(params[0]);
    },
  },
  arrows: {
    getWord(_wordset, wordIndex): string {
      return DDR.chart2Word(wordIndex === 0);
    },
    rememberSettings(): void {
      save("highlightMode", Config.highlightMode);
    },
    getEmulatedChar(event: KeyboardEvent): string | null {
      const ekey = event.key;
      if (ekey === "a" || ekey === "ArrowLeft" || ekey === "j") {
        return "←";
      }
      if (ekey === "s" || ekey === "ArrowDown" || ekey === "k") {
        return "↓";
      }
      if (ekey === "w" || ekey === "ArrowUp" || ekey === "i") {
        return "↑";
      }
      if (ekey === "d" || ekey === "ArrowRight" || ekey === "l") {
        return "→";
      }
      return null;
    },
  },
  rAnDoMcAsE: {
    alterText(word: string): string {
      let randomCaseWord = "";

      for (let letter of word) {
        if (Math.random() < 0.5) {
          randomCaseWord += letter.toUpperCase();
        } else {
          randomCaseWord += letter.toLowerCase();
        }
      }

      return randomCaseWord;
    },
  },
  sPoNgEcAsE: {
    alterText(word: string): string {
      let spongeCaseWord = "";

      for (let i = 0; i < word.length; i++) {
        if (i % 2 === 0) {
          spongeCaseWord += word[i]?.toLowerCase();
        } else {
          spongeCaseWord += word[i]?.toUpperCase();
        }
      }

      return spongeCaseWord;
    },
  },
  rot13: {
    alterText(word: string): string {
      let alphabet = "abcdefghijklmnopqrstuvwxyz";

      let rot13Word = "";

      for (let ch of word) {
        let chIndex = alphabet.indexOf(ch.toLowerCase());
        if (chIndex === -1) {
          rot13Word += ch;
          continue;
        }

        let rot13Ch = (chIndex + 13) % 26;
        if (ch.toUpperCase() === ch) {
          rot13Word += alphabet[rot13Ch]?.toUpperCase();
        } else {
          rot13Word += alphabet[rot13Ch];
        }
      }

      return rot13Word;
    },
  },
  backwards: {
    alterText(word: string): string {
      return word.split("").reverse().join("");
    },
  },
  capitals: {
    alterText(word: string): string {
      return Strings.capitalizeFirstLetterOfEachWord(word);
    },
  },
  layout_mirror: {
    applyConfig(): void {
      let layout = Config.layout;
      if (Config.layout === "default") {
        layout = "qwerty";
      }
      setConfig("layout", layout, {
        nosave: true,
      });
      setConfig("keymapLayout", "overrideSync", {
        nosave: true,
      });
    },
    rememberSettings(): void {
      save("keymapMode", Config.keymapMode);
      save("layout", Config.layout);
    },
  },
  layoutfluid: {
    applyConfig(): void {
      const layout = Config.customLayoutfluid[0] ?? "qwerty";

      setConfig("layout", layout as Layout, {
        nosave: true,
      });
      setConfig("keymapLayout", layout as KeymapLayout, {
        nosave: true,
      });
    },
    rememberSettings(): void {
      save("keymapMode", Config.keymapMode);
      save("layout", Config.layout);
      save("keymapLayout", Config.keymapLayout);
    },
    handleSpace(input): void {
      if (Config.mode !== "time") {
        const layouts = Config.customLayoutfluid;
        const outOf: number = TestWords.words.length;
        const wordsPerLayout = Math.floor(outOf / layouts.length);
        const index = Math.floor((getActiveWordIndex() + 1) / wordsPerLayout);
        const mod =
          wordsPerLayout - ((getActiveWordIndex() + 1) % wordsPerLayout);

        if (layouts[index] as string) {
          if (mod <= 3 && (layouts[index + 1] as string)) {
            const nextLayout = Strings.capitalizeFirstLetter(
              (layouts[index + 1] as string).replace(/_/g, " "),
            );
            setLayoutfluidTimerText(
              mod === 1
                ? `${nextLayout} starting next word`
                : `${nextLayout} in: ${mod} words`,
            );
            setLayoutfluidTimerVisible(true);
          } else {
            setLayoutfluidTimerVisible(false);
          }
          if (mod === wordsPerLayout) {
            setConfig("layout", layouts[index] as Layout);
            setConfig("keymapLayout", layouts[index] as KeymapLayout);
            if (mod > 3) {
              setLayoutfluidTimerVisible(false);
            }
          }
        } else {
          setLayoutfluidTimerVisible(false);
        }
        setTimeout(() => {
          highlightKeymapKey(
            TestWords.words.getCurrent()?.text.charAt(input.current().length) ??
              "",
          );
        }, 1);
      }
    },
    getResultContent(): string {
      return Config.customLayoutfluid.join(" ");
    },
  },
  gibberish: {
    getWord(): string {
      return GetText.getGibberish();
    },
  },
  ascii: {
    getWord(): string {
      return GetText.getASCII();
    },
  },
  specials: {
    getWord(): string {
      return GetText.getSpecials();
    },
  },
  read_ahead_easy: {
    rememberSettings(): void {
      save("highlightMode", Config.highlightMode);
    },
    async handleKeydown(event, input): Promise<void> {
      await readAheadHandleKeydown(event, input);
    },
  },
  read_ahead: {
    rememberSettings(): void {
      save("highlightMode", Config.highlightMode);
    },
    async handleKeydown(event, input): Promise<void> {
      await readAheadHandleKeydown(event, input);
    },
  },
  read_ahead_hard: {
    rememberSettings(): void {
      save("highlightMode", Config.highlightMode);
    },
    async handleKeydown(event, input): Promise<void> {
      await readAheadHandleKeydown(event, input);
    },
  },
  memory: {
    applyConfig(): void {
      setWordsWrapperHidden(true);
      setConfig("showAllLines", true, {
        nosave: true,
      });
      if (Config.keymapMode === "next") {
        setConfig("keymapMode", "react", {
          nosave: true,
        });
      }
    },
    rememberSettings(): void {
      save("mode", Config.mode);
      save("showAllLines", Config.showAllLines);
      if (Config.keymapMode === "next") {
        save("keymapMode", Config.keymapMode);
      }
    },
    start(): void {
      setMemoryTimerDuration(null);
      setWordsHidden(true);
    },
    restart(): void {
      setMemoryTimerDuration(Math.round(Math.pow(TestWords.words.length, 1.2)));
      setWordsHidden(false);
      if (Config.keymapMode === "next") {
        setConfig("keymapMode", "react");
      }
    },
  },
  nospace: {
    rememberSettings(): void {
      save("highlightMode", Config.highlightMode);
    },
  },
  poetry: {
    async pullSection(): Promise<JSONData.Section | false> {
      return getPoem();
    },
  },
  wikipedia: {
    async pullSection(lang?: Language): Promise<JSONData.Section | false> {
      return getSection((lang ?? "") || "english");
    },
  },
  weakspot: {
    getWord(wordset?: Wordset): string {
      if (wordset !== undefined) return WeakSpot.getWord(wordset);
      else return "";
    },
  },
  pseudolang: {
    async withWords(words?: string[]): Promise<Wordset> {
      if (words !== undefined) return new PseudolangWordGenerator(words);
      return new Wordset([]);
    },
  },
  IPv4: {
    getWord(): string {
      return IPAddresses.getRandomIPv4address();
    },
    punctuateWord(word: string): string {
      let w = word;
      if (Math.random() < 0.25) {
        w = IPAddresses.addressToCIDR(word);
      }
      return w;
    },
    rememberSettings(): void {
      save("numbers", Config.numbers);
    },
  },
  IPv6: {
    getWord(): string {
      return IPAddresses.getRandomIPv6address();
    },
    punctuateWord(word: string): string {
      let w = word;
      if (Math.random() < 0.25) {
        w = IPAddresses.addressToCIDR(word);
      }
      // Compress
      if (w.includes(":")) {
        w = IPAddresses.compressIpv6(w);
      }
      return w;
    },
    rememberSettings(): void {
      save("numbers", Config.numbers);
    },
  },
  binary: {
    getWord(): string {
      return GetText.getBinary();
    },
  },
  hexadecimal: {
    getWord(): string {
      return GetText.getHexadecimal();
    },
    punctuateWord(word: string): string {
      return `0x${word}`;
    },
    rememberSettings(): void {
      save("punctuation", Config.punctuation);
    },
  },
  zipf: {
    getWordsFrequencyMode(): FunboxWordsFrequency {
      return "zipf";
    },
  },
  ddoouubblleedd: {
    alterText(word: string): string {
      return word.replace(/./gu, "$&$&");
    },
  },
  instant_messaging: {
    alterText(word: string): string {
      return word
        .toLowerCase()
        .replace(/[.!?]$/g, "\n") //replace .?! with enter
        .replace(/[().'"]/g, "") //remove special characters
        .replace(/\n+/g, "\n"); //make sure there is only one enter
    },
  },
  morse: {
    alterText(word: string): string {
      return GetText.getMorse(word);
    },
  },
  underscore_spaces: {
    alterText(word: string, wordIndex: number, limit: number): string {
      if (wordIndex === limit - 1) return word; // don't add underscore to the last word
      return `${word}_`;
    },
  },
  ALL_CAPS: {
    alterText(word: string): string {
      return word.toUpperCase();
    },
  },
  polyglot: {
    async withWords(_words) {
      const promises = Config.customPolyglot.map(async (language) =>
        JSONData.getLanguage(language).catch(() => {
          showNoticeNotification(
            `Failed to load language: ${language}. It will be ignored.`,
          );
          return null;
        }),
      );

      const languages = (await Promise.all(promises)).filter(
        (lang): lang is LanguageObject => lang !== null,
      );

      if (languages.length === 0) {
        toggleFunbox("polyglot");
        throw new Error(
          `No valid languages found. Please check your polyglot languages config (${Config.customPolyglot.join(
            ", ",
          )}).`,
        );
      }

      if (languages.length === 1) {
        const lang = languages[0] as LanguageObject;
        setConfig("language", lang.name, {
          nosave: true,
        });
        toggleFunbox("polyglot", true);
        showNoticeNotification(
          `Disabled polyglot funbox because only one valid language was found. Check your polyglot languages config (${Config.customPolyglot.join(
            ", ",
          )}).`,
          {
            durationMs: 7000,
          },
        );
        throw new WordGenError("");
      }

      // direction conflict check
      const allRightToLeft = languages.every((lang) => lang.rightToLeft);
      const allLeftToRight = languages.every((lang) => !lang.rightToLeft);
      const mainLanguage = await JSONData.getLanguage(Config.language);
      const mainLanguageIsRTL = mainLanguage?.rightToLeft ?? false;
      if (
        (mainLanguageIsRTL && allLeftToRight) ||
        (!mainLanguageIsRTL && allRightToLeft)
      ) {
        const fallbackLanguage =
          languages[0]?.name ?? (allRightToLeft ? "arabic" : "english");
        setConfig("language", fallbackLanguage);
        showNoticeNotification(
          `Language direction conflict: switched to ${fallbackLanguage} for consistency.`,
          { durationMs: 5000 },
        );
        throw new WordGenError("");
      }

      // build languageProperties
      const languageProperties = new Map(
        languages.map((lang) => [
          lang.name,
          {
            noLazyMode: lang.noLazyMode,
            joiningScript: lang.joiningScript,
            rightToLeft: lang.rightToLeft,
            additionalAccents: lang.additionalAccents,
          },
        ]),
      );

      const wordsWithLanguage = new Map(
        languages.flatMap((lang) =>
          lang.words.map((word) => [word, lang.name]),
        ),
      );

      return new PolyglotWordset(wordsWithLanguage, languageProperties);
    },
  },
};

export function getFunboxFunctions(): Record<FunboxName, FunboxFunctions> {
  return list as Record<FunboxName, FunboxFunctions>;
}
